/* eslint-disable prefer-arrow/prefer-arrow-functions */
// util/distributePaxOverCombo.js

// --- helpers de typage & capacité ---
function inferPaxFromValue(valueStr) {
  const s = String(valueStr || '');
  const mPax = s.match(/pax\s*:\s*(\d+)/i);

  if (mPax) return Number(mPax[1]);
  const mA = s.match(/adt\s*:\s*(\d+)/i);
  const mC = s.match(/chd\s*:\s*(\d+)/i);

  if (mA || mC) return Number(mA?.[1] || 0) + Number(mC?.[1] || 0);

  return null;
}
function parseACIText(s = '') {
  const t = String(s || '');
  let m = /\((\d+)A[ ,]+(\d+)C[ ,]+(\d+)I\)/i.exec(t);

  if (m) return { A: +m[1], C: +m[2], I: +m[3] };
  m = /adt[:=]\s*(\d+)\s*,\s*chd[:=]\s*(\d+)\s*,\s*inf[:=]\s*(\d+)/i.exec(t);
  if (m) return { A: +m[1], C: +m[2], I: +m[3] };

  return { A: 0, C: 0, I: 0 };
}

function baseTypeOf(c) {
  const t0 = String(c.baseType || '')
    .toLowerCase()
    .trim();

  if (t0) return t0;

  // 1) reconnaissance directe par code
  const code = String(c.code || '').toLowerCase();

  if (/single|sgl/.test(code)) return 'single';
  if (/double|dbl|twin/.test(code)) return 'double';
  if (/triple|tpl|trpl|trp/.test(code)) return 'triple';

  // 2) libellé
  const d = String(c.desc || c.description || '').toLowerCase();

  if (/single|sgl/.test(d)) return 'single';
  if (/double|dbl|twin/.test(d)) return 'double';
  if (/triple|tpl|trpl|trp|3\s*(pax|pers|beds?)?/.test(d)) return 'triple';

  // 3) capacité ou value
  const paxCap = Number(
    c?.capacities?.pax ?? c?.occupancies?.maxPerson ?? c?.maxPerson ?? NaN,
  );

  if (paxCap === 1) return 'single';
  if (paxCap === 2) return 'double';
  if (paxCap === 3) return 'triple';
  const paxFromVal = inferPaxFromValue(c?.value);

  if (paxFromVal === 1) return 'single';
  if (paxFromVal === 2) return 'double';
  if (paxFromVal === 3) return 'triple';

  return 'autre';
}

const capOf = (r) => {
  // vrai cap si fourni, sinon par type
  const fromData = Number(r?.capacities?.pax ?? r?.occupancies?.maxPerson ?? NaN);

  if (Number.isFinite(fromData) && fromData > 0) return fromData;

  return r.baseType === 'single'
    ? 1
    : r.baseType === 'double'
    ? 2
    : r.baseType === 'triple'
    ? 3
    : 4;
};

// --- PATCH: Répartition robuste ---
export function distributePaxOverCombo(combo = [], reqA = 0, reqC = 0, reqI = 0) {
  // 0) Déplier qty>1 et typer dès maintenant
  const expanded = [];

  for (const c of Array.isArray(combo) ? combo : []) {
    const qty = Math.max(1, Number(c.qty || 1));

    for (let i = 0; i < qty; i++) {
      const bt = baseTypeOf(c);
      // garde-fou : si jamais on n’a pas reconnu, refaire un check regex
      const bt2 =
        bt === 'autre' && /double|dbl|twin/i.test(String(c.code || c.description || ''))
          ? 'double'
          : bt;

      expanded.push({ ...c, qty: 1, baseType: bt2, allocation: null });
    }
  }

  let A = Number(reqA || 0);
  let C = Number(reqC || 0);
  // les bébés ne jouent pas sur la capacité ici

  // 1) Singles (1A) si possible
  for (const r of expanded) {
    if (r.baseType !== 'single' || r.allocation) continue;
    const take = Math.min(1, A);

    r.allocation = { adults: take, children: 0, infant: 0 };
    A -= take;
  }

  // 2) Triples
  for (const r of expanded) {
    if (r.baseType !== 'triple' || r.allocation) continue;
    if (A >= 2 && C >= 1) {
      r.allocation = { adults: 2, children: 1, infant: 0 };
      A -= 2;
      C -= 1;
    } else if (A >= 1 && C >= 2) {
      r.allocation = { adults: 1, children: 2, infant: 0 };
      A -= 1;
      C -= 2;
    } else if (A >= 3) {
      r.allocation = { adults: 3, children: 0, infant: 0 };
      A -= 3;
    } else {
      const a = Math.min(2, A);
      const ch = Math.min(3 - a, C);

      r.allocation = { adults: a, children: ch, infant: 0 };
      A -= a;
      C -= ch;
    }
  }

  // 3) Doubles : **uniquement** 1A+1C sinon 2A
  for (const r of expanded) {
    if (r.baseType !== 'double' || r.allocation) continue;
    if (A >= 1 && C >= 1) {
      r.allocation = { adults: 1, children: 1, infant: 0 };
      A -= 1;
      C -= 1;
    } else {
      const a = Math.min(2, A);

      r.allocation = { adults: a, children: 0, infant: 0 };
      A -= a;
    }
  }

  // 4) SÉCURITÉ & REMPLISSAGE FINAL
  for (const r of expanded) {
    // a) si toujours vide, tenter de lire l’ACI dans desc/value
    if (!r.allocation || (r.allocation.adults || 0) + (r.allocation.children || 0) === 0) {
      const aci = parseACIText(r.desc || r.description || r.value || '');

      if (aci.A + aci.C > 0)
        r.allocation = { adults: aci.A, children: aci.C, infant: aci.I || 0 };
    }

    // b) jamais dépasser la capacité + pas d’enfant seul
    const cap = capOf(r);
    let a = Number(r.allocation?.adults || 0);
    let c = Number(r.allocation?.children || 0);

    while (a + c > cap) {
      if (c > 0) c--;
      else if (a > 0) a--;
      else break;
    }
    if (c > 0 && a === 0) {
      // enfant seul
      if (A > 0) {
        a++;
        A--;
      } // ajouter 1 adulte si possible
      else {
        c--;
        C++;
      } // sinon, remettre l’enfant au stock
    }
    r.allocation = { adults: a, children: c, infant: 0 };
  }

  // 5) LOG DEBUG (à retirer si besoin)
  console.log('[distribute] input A/C/I =', reqA, reqC, reqI);
  console.table(
    expanded.map((r, i) => ({
      idx: i + 1,
      baseType: r.baseType,
      code: r.code,
      desc: r.description,
      A: r.allocation?.adults || 0,
      C: r.allocation?.children || 0,
      cap: capOf(r),
    })),
  );

  expanded.forEach((r) => {
    const a = r.allocation?.adults ?? 0;
    const c = r.allocation?.children ?? 0;
    const i = r.allocation?.infants ?? r.allocation?.infant ?? 0;
    const pax = a + c + i;

    const roomRef = r.roomRef || r.code; // ✅ priorité au vrai code PGI

    r.value = `${roomRef}|adt:${a},chd:${c},inf:${i},pax:${pax}`;
    r.pgiRoomCode = r.value;

    // optionnel: harmoniser le code affiché
    r.code = roomRef;
  });

  return expanded;
}
